import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Calendar, MapPin } from 'lucide-react';
import eventsApi from 'api/requests/events';
import EventProvider, { useEvent } from 'context/events/Event';
import Checkin from '../Checkin';
import ParticipantsList from 'modules/dashboard/events/detail/participants/ParticipantsList';
import findSessionInfo from './eventSession';
import Loader from 'components/_UI/Loader';
import LinkPage from 'components/_UI/LinkPage';
import { FullyCentered } from 'styled/alignment/Center';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import date from 'helpers/date';
import { canInEvent, EVENT_PERMISSIONS } from 'helpers/eventCollaboration';
import paths from 'routes/paths';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

// Camada externa: só monta o EventProvider. Não dá pra chamar useEvent() aqui
// mesmo — o provider ainda não é ancestral neste ponto da árvore.
export default function ActiveEventCheckin({ event }) {
    return (
        <EventProvider>
            <ActiveEventCheckinContent event={event} />
        </EventProvider>
    );
}

function ActiveEventCheckinContent({ event }) {
    const { event: fullEvent, setEvent } = useEvent();
    const [loadError, setLoadError] = useState(false);

    const loadEvent = useCallback(async () => {
        setLoadError(false);

        try {
            const { data } = await eventsApi.getBySlug(event.slug);
            setEvent(data?.data);
        } catch (error) {
            setLoadError(true);
        }
    }, [event.slug]);

    useEffect(() => {
        loadEvent();
    }, [loadEvent]);

    const ready = fullEvent?.slug === event.slug;
    const session = findSessionInfo(event.dates);

    return (
        <Wrapper>
            <EventHeader>
                <HeaderAccent />
                <HeaderIcon>
                    <Calendar size={20} color={panelAccent(colors.secondaryPurple)} />
                </HeaderIcon>
                <HeaderText>
                    <EventName>{event.name}</EventName>
                    <EventMeta>
                        {session && (
                            <MetaItem>
                                <Calendar size={13} />
                                {session.isToday ? 'Hoje' : date.format(session.date)}
                                {session.start && `, ${date.getTimeWithoutSeconds(session.start)}`}
                            </MetaItem>
                        )}
                        <MetaItem>
                            <MapPin size={13} />
                            {formatAddress(event.address)}
                        </MetaItem>
                    </EventMeta>
                </HeaderText>
                <HeaderLinkWrap>
                    <LinkPage isWrapper to={`${paths.dashboard.events}?detail=${event.slug}`} target='_SELF'>
                        Ver evento completo
                    </LinkPage>
                </HeaderLinkWrap>
            </EventHeader>

            <Checkin minimal hideHeader />

            {!canInEvent(event, EVENT_PERMISSIONS.PARTICIPANTS_VIEW) ? null : ready ? (
                <ParticipantsList data={fullEvent.slug} />
            ) : loadError ? (
                <ErrorState>
                    <Text>Não foi possível carregar a lista de participantes deste evento.</Text>
                    <RetryButton type='button' onClick={loadEvent}>Tentar novamente</RetryButton>
                </ErrorState>
            ) : (
                <FullyCentered height='160px'>
                    <Loader />
                </FullyCentered>
            )}
        </Wrapper>
    );
}

function formatAddress(address) {
    if (!address) return 'Local a definir';

    const { street, number, complement } = address;

    return [
        street?.name && `${street.name},`,
        street?.district && `Bairro ${street.district}`,
        number && `nº ${number}`,
        complement && `- ${complement}`,
    ].filter(Boolean).join(' ');
}

const Wrapper = styled.section`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const EventHeader = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    padding: 20px;
    position: relative;
    overflow: hidden;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 24px 28px;
        flex-wrap: nowrap;
    }
`;

const HeaderAccent = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${panelAccent(colors.secondaryPurple)};
    border-radius: 12px 0 0 12px;
`;

const HeaderIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: ${panelAccentAlpha(colors.secondaryPurple, '10')};
    flex-shrink: 0;
`;

const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
    flex: 1;
`;

const EventName = styled.h2`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: ${colors.nightDark};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const EventMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 16px;
`;

const MetaItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: ${colors.night};

    & > svg {
        flex-shrink: 0;
    }
`;

const HeaderLinkWrap = styled.div`
    flex-shrink: 0;
    margin-left: auto;
`;

const ErrorState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 32px 20px;
    text-align: center;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
`;

const Text = styled.p`
    margin: 0;
    font-size: 14px;
    color: ${colors.night};
`;

const RetryButton = styled.button`
    padding: 8px 18px;
    border: 1.5px solid ${panelAccentAlpha(colors.secondaryPurple, '44')};
    border-radius: 10px;
    background: transparent;
    color: ${panelAccent(colors.secondaryPurple)};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: ${panelAccentAlpha(colors.secondaryPurple, '08')};
    }
`;
